import { Link } from "react-router-dom";
import TrackedLink from "@/components/TrackedLink";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  ArrowRight,
  Phone,
  Euro,
  Zap,
  MapPin,
  Smartphone,
  Headphones,
  CheckCircle,
  PhoneCall,
  ClipboardList,
  Truck,
  Plug,
  PartyPopper,
} from "lucide-react";
import heroBg from "@/assets/telefonie-overstappen-bg.jpg";

const benefits = [
  {
    icon: Euro,
    title: "Lagere kosten",
    desc: "VoIP is aanzienlijk goedkoper dan traditionele ISDN- of analoge telefonie. Bespaar op zowel de maandelijkse kosten als gesprekstarieven — zeker bij internationaal bellen.",
    color: "bg-hero-gradient",
  },
  {
    icon: Zap,
    title: "Altijd de nieuwste functies",
    desc: "Geen dure hardware-upgrades meer. Nieuwe functies zoals nachtschakeling, IVR of CRM-koppeling worden gewoon via de cloud toegevoegd — gratis en direct beschikbaar.",
    color: "bg-rinqel-blue-gradient",
  },
  {
    icon: MapPin,
    title: "Werken vanaf elke locatie",
    desc: "Bel vanuit kantoor, thuis of onderweg met hetzelfde nummer. Je team is altijd bereikbaar — of ze nu op het terras zitten of in de vergaderzaal.",
    color: "bg-rinqel-green-gradient",
  },
  {
    icon: Smartphone,
    title: "Bellen op elk apparaat",
    desc: "Gebruik je bureau-telefoon, laptop of smartphone. Alles werkt samen in één systeem. Klanten merken geen verschil — jij wel.",
    color: "bg-hero-gradient",
  },
  {
    icon: Headphones,
    title: "Professionele uitstraling",
    desc: "Keuzemenu's, wachtrijen, naamweergave en voicemail naar e-mail: functies die je bedrijf meteen groter laten overkomen — ook als je klein bent.",
    color: "bg-rinqel-blue-gradient",
  },
  {
    icon: Phone,
    title: "Jouw bestaande nummers",
    desc: "Je huidige telefoonnummers neem je gewoon mee. Wij regelen de nummerdraagbaarheid volledig voor je — geen gedoe, geen nummerverlies.",
    color: "bg-rinqel-green-gradient",
  },
];

const steps = [
  {
    number: "01",
    icon: PhoneCall,
    title: "Maak een afspraak",
    desc: "Je belt ons op of stuurt een berichtje. We plannen een kort gesprek om te kijken wat jij nodig hebt.",
    color: "bg-hero-gradient",
  },
  {
    number: "02",
    icon: ClipboardList,
    title: "Inventarisatie op locatie",
    desc: "Een Rinqel-medewerker komt langs om je huidige situatie in kaart te brengen: hoeveel aansluitingen, welke nummers, hoe is je netwerk ingericht.",
    color: "bg-rinqel-blue-gradient",
  },
  {
    number: "03",
    icon: Truck,
    title: "Voorbereiding & levering",
    desc: "Wij regelen alles: porteer je nummers, configureer de centrale en sturen eventuele toestellen op. Jij hoeft niets te doen.",
    color: "bg-rinqel-green-gradient",
  },
  {
    number: "04",
    icon: Plug,
    title: "Aansluiten & instellen",
    desc: "Op de afgesproken datum sluiten we alles aan. We testen elke lijn en instelling — tot alles perfect werkt.",
    color: "bg-hero-gradient",
  },
  {
    number: "05",
    icon: PartyPopper,
    title: "Klaar! En wij blijven beschikbaar",
    desc: "Je telefonie werkt. Vragen? Wij zijn er voor je — telefonisch, via mail of op locatie. Maandelijks opzegbaar, dus je zit nergens aan vast.",
    color: "bg-rinqel-blue-gradient",
  },
];

const faqs = [
  {
    q: "Kan ik mijn bestaande nummers meenemen?",
    a: "Ja, altijd. Nummerdraagbaarheid regelen wij volledig voor je. Jouw huidige nummers blijven gewoon bereikbaar — klanten merken niets.",
  },
  {
    q: "Wat heb ik nodig om over te stappen?",
    a: "Een stabiele internetverbinding en dat is het. Wij leveren toestellen of je gebruikt de softphone-app op je laptop of telefoon. Hardware is optioneel.",
  },
  {
    q: "Hoe lang duurt het overstappen?",
    a: "Gemiddeld 1 tot 2 weken, afhankelijk van de grootte van je organisatie. De nummerdraagbaarheid bepaalt vaak de planning.",
  },
  {
    q: "Zijn er storingen als ik overstap?",
    a: "We plannen de overstap zorgvuldig zodat er geen onderbrekingen zijn. In de meeste gevallen verloopt de overstap volledig ongemerkt voor jou en je bellers.",
  },
  {
    q: "Zit ik aan een contract vast?",
    a: "Nee. Rinqel telefonie is maandelijks opzegbaar. Geen langlopende contracten, geen boetes. Gewoon eerlijk.",
  },
];

const TelefonieOverstappen = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[480px] overflow-hidden">
        <img
          src={heroBg}
          alt="Overstappen naar Rinqel VoIP telefonie"
          loading="eager" decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/25" />

        <div className="container relative z-10 flex min-h-[480px] items-center py-20">
          <div className="max-w-2xl">
            <Link
              to="/diensten/telefonie"
              className="mb-6 inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Terug naar Telefonie
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Maandelijks opzegbaar · Geen gedoe
              </span>
            </motion.div>

            <motion.h1
              className="mt-4 text-4xl font-bold leading-[1.1] tracking-tight text-white md:text-6xl"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Overstappen naar{" "}
              <span className="text-gradient">Rinqel telefonie</span>
            </motion.h1>

            <motion.p
              className="mt-5 max-w-xl text-lg leading-relaxed text-white/80"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Moderne VoIP-telefonie voor minder geld, meer functies en meer vrijheid. Wij regelen de hele overstap — inclusief je bestaande nummers.
            </motion.p>

            <motion.div
              className="mt-10 flex flex-wrap gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold" size="lg" asChild>
                <TrackedLink to="/contact" trackLabel="Gratis adviesgesprek" trackLocation="telefonie-overstappen-hero">Gratis adviesgesprek <ArrowRight className="h-4 w-4" /></TrackedLink>
              </Button>
              <Button className="border border-white/30 bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm font-semibold" size="lg" asChild>
                <TrackedLink to="/configurator" trackLabel="Bereken je prijs" trackLocation="telefonie-overstappen-hero">Bereken je prijs</TrackedLink>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Voordelen */}
      <section className="py-16 md:py-24 bg-hero-gradient-subtle">
        <div className="container">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-1 w-10 rounded-full bg-hero-gradient" />
              <span className="text-xs font-semibold uppercase tracking-widest text-primary">Waarom overstappen?</span>
            </div>
            <h2 className="text-2xl font-bold md:text-3xl">
              6 redenen waarom bedrijven overstappen naar{" "}
              <span className="text-gradient">VoIP</span>
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl">
              ISDN wordt uitgefaseerd. Analoge lijnen zijn duur en beperkt. VoIP is de logische stap — en Rinqel maakt die stap zo makkelijk mogelijk.
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.map((b) => {
              const Icon = b.icon;
              return (
                <div
                  key={b.title}
                  className="relative overflow-hidden flex flex-col gap-4 rounded-2xl border border-border bg-card p-6 shadow-card"
                >
                  <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${b.color}`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-base">{b.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{b.desc}</p>
                  </div>
                  <CheckCircle className="absolute bottom-4 right-4 h-5 w-5 text-rinqel-green opacity-30" />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stappenplan */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-1 w-10 rounded-full bg-rinqel-blue-gradient" />
              <span className="text-xs font-semibold uppercase tracking-widest text-secondary">Hoe werkt het?</span>
            </div>
            <h2 className="text-2xl font-bold md:text-3xl">
              Van aanvraag tot{" "}
              <span className="text-gradient">live</span>
              {" "}— in 5 stappen
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl">
              Wij regelen alles voor je. Jij hoeft alleen "ja" te zeggen — de rest doen wij.
            </p>
          </div>

          <div className="relative">
            {/* Vertical connector line */}
            <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-border hidden md:block" />

            <div className="flex flex-col gap-6">
              {steps.map((step) => {
                const Icon = step.icon;
                return (
                  <div key={step.number} className="flex gap-6 items-start">
                    {/* Step icon */}
                    <div className={`relative shrink-0 flex h-12 w-12 items-center justify-center rounded-full ${step.color} shadow-card z-10`}>
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    {/* Content */}
                    <div className="flex-1 rounded-2xl border border-border bg-card p-6 shadow-card">
                      <div className="flex items-start gap-3">
                        <span className="text-2xl font-black text-muted-foreground/30 leading-none">{step.number}</span>
                        <div>
                          <h3 className="font-bold text-base">{step.title}</h3>
                          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{step.desc}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-hero-gradient-subtle">
        <div className="container max-w-3xl">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-1 w-10 rounded-full bg-rinqel-green-gradient" />
              <span className="text-xs font-semibold uppercase tracking-widest text-rinqel-green">Veelgestelde vragen</span>
            </div>
            <h2 className="text-2xl font-bold md:text-3xl">
              Vragen over overstappen?
            </h2>
          </div>

          <div className="flex flex-col gap-4">
            {faqs.map((faq, i) => {
              const colors = ["bg-hero-gradient", "bg-rinqel-blue-gradient", "bg-rinqel-green-gradient"];
              const color = colors[i % 3];
              return (
                <div
                  key={faq.q}
                  className="rounded-2xl border border-border bg-card shadow-card overflow-hidden"
                >
                  <div className={`${color} px-6 py-4`}>
                    <p className="font-bold text-white text-sm">{faq.q}</p>
                  </div>
                  <div className="px-6 py-4">
                    <p className="text-sm leading-relaxed text-muted-foreground">{faq.a}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container">
          <div className="relative overflow-hidden rounded-2xl bg-hero-gradient p-10 md:p-14 text-center shadow-card">
            <div className="absolute -right-10 -top-10 h-56 w-56 rounded-full bg-white/10" />
            <div className="absolute -left-6 bottom-0 h-36 w-36 rounded-full bg-white/5" />
            <div className="relative">
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                Klaar om te stappen?
              </h2>
              <p className="mt-3 text-white/80 max-w-xl mx-auto">
                Vraag een gratis adviesgesprek aan. We kijken samen naar jouw situatie — helemaal vrijblijvend.
              </p>
              <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
                <Button className="bg-white text-primary hover:bg-white/90 font-semibold" size="lg" asChild>
                  <TrackedLink to="/contact" trackLabel="Gratis adviesgesprek" trackLocation="telefonie-overstappen-cta">Gratis adviesgesprek <ArrowRight className="h-4 w-4" /></TrackedLink>
                </Button>
                <Button className="border border-white/30 bg-white/10 text-white hover:bg-white/20 font-semibold" size="lg" asChild>
                  <TrackedLink to="/configurator" trackLabel="Bereken je prijs" trackLocation="telefonie-overstappen-cta">Bereken je prijs</TrackedLink>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default TelefonieOverstappen;
